/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {defineMessage, defineMessages} from 'react-intl'
import {
    DEFAULT_LIMIT_VALUES,
    DEFAULT_SEARCH_PARAMS,
} from '@salesforce/retail-react-app/app/constants'

DEFAULT_LIMIT_VALUES[0] = 16
DEFAULT_SEARCH_PARAMS.limit = 16

const ACTIVE_DATA_ENABLED = true

export const overridenMessagesPlural = defineMessages({
    orderSummaryCartItemSingle: {
        id: 'order_summary.cart_items.action.num_of_items_in_cart',
        description: 'Message for a single item in the cart',
        defaultMessage: '{itemCount} item in cart',
    },
    orderSummaryCartItemsPlural: {
        id: 'order_summary.cart_items.action.num_of_items_in_cart_plural',
        description: 'Message for multiple items in the cart',
        defaultMessage: '{itemCount} items in cart',
    },
})

// Ensure totalItems is defined or passed as a parameter where this message is used

export {DEFAULT_LIMIT_VALUES, DEFAULT_SEARCH_PARAMS, ACTIVE_DATA_ENABLED}

export const TOAST_MESSAGE_ADDED_TO_WISHLIST = defineMessage({
    id: 'global.info.added_to_wishlist',
    defaultMessage: 'Added to favorites',
})

export const TOAST_MESSAGE_ADDED_TO_WISHLIST_DESCRIPTION = defineMessage({
    defaultMessage:
        'Warning: your wishlist is temporary! Complete your purchase or log in to save your favorite items before they expire',
    id: 'global.info.added_to_favorites_description',
})

export const TOAST_MESSAGE_GO_TO_WISHLIST = defineMessage({
    id: 'wishlist.temporary.to-wishlist',
    defaultMessage: 'Go to wishlist',
})

export const API_ERROR_MESSAGE = defineMessage({
    id: 'global.error.something_went_wrong',
    defaultMessage: 'Something went wrong. Try again!',
})

export const ADD_TO_CART_ERROR_MESSAGES = {
    NO_VARIANT_SELECTED: defineMessage({
        defaultMessage: 'Please select all your options above',
        id: 'general.message.select_your_options',
    }),
    INVENTORY_REMAINING_FOR_PRODUCT: defineMessage({
        defaultMessage: 'Only {stockLevel} left for {productName}!',
        id: 'general.message.inventory_remaining_for_product',
    }),
    OUT_OF_STOCK_FOR_PRODUCT: defineMessage({
        defaultMessage: 'Out of stock for {productName}',
        id: 'general.message.out_of_stock_for_product',
    }),
}

export const ADD_TO_CART_ERROR_KEYS = {
    NO_VARIANT_SELECTED: 1,
    INVENTORY_REMAINING_FOR_PRODUCT: 2,
    OUT_OF_STOCK_FOR_PRODUCT: 3,
}

export const MOCKED_RECOMMENDED = 'EC9155.YE,EN640.B,ECAM320.70.TB,ECAM322.70.SB,ECAM320.70.TB'
export const MOCKED_RECOMMENDED_ARRAY = [
    'EC9155.YE',
    'EN640.B',
    'ECAM320.70.TB',
    'ECAM322.70.SB',
    'ECAM320.70.TB',
]
export const PRODUCT_CUSTOM_SELECTABLE_ATTRIBUTE_ID = 'subModelIdentifier'
export const PRODUCT_CUSTOM_EXCLUDE_ATTRIBUTE_ID = 'skuIdentifier'

// Constants for shopper context
// Supported non-string field types used in SHOPPER_CONTEXT_SEARCH_PARAMS below.
// Only non-string types need to be identified using the "type" field.
// If no "type" field is present, the value will be parsed as a string by default.
export const SHOPPER_CONTEXT_FIELD_TYPES = {
    INT: 'int',
    DOUBLE: 'double',
    ARRAY: 'array',
}
export const SHOPPER_CONTEXT_SEARCH_PARAMS = {
    sourceCode: {paramName: 'cluid'},
    geoLocation: {
        city: {paramName: 'city'},
        country: {paramName: 'country'},
        countryCode: {paramName: 'countryCode'},
        latitude: {paramName: 'latitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        longitude: {paramName: 'longitude', type: SHOPPER_CONTEXT_FIELD_TYPES.DOUBLE},
        metroCode: {paramName: 'metroCode'},
        postalCode: {paramName: 'postalCode'},
        region: {paramName: 'region'},
        regionCode: {paramName: 'regionCode'},
    },
    customQualifiers: {
        countryCode: {paramName: 'countryCode'},
    },
    assignmentQualifiers: {
        // Add assignment qualifiers here
    },
}

export const METRIC7_MULTIPLIER = 24.04

export const MAX_CACHE_AGE_PLP = 60 * 60 * 2 // 2 hours in seconds
export const STALE_WHILE_REVALIDATE_PLP = 60 * 60 * 2 // 2 hours in seconds

export const MAX_CACHE_AGE_PDP = 60 * 30 // 30 minutes in seconds
export const STALE_WHILE_REVALIDATE_PDP = 60 * 30 // 30 minutes in seconds

export const MAX_CACHE_AGE_HP = 60 * 60 * 2 // 2 hours in seconds
export const STALE_WHILE_REVALIDATE_HP = 60 * 60 * 2 // 24 hours in seconds

export const COUNTRY_CONTACTS = {
    AU: '1800 126 659',
    AT: '0800 252257',
    BH: '(+973) 17228887',
    BE: 'Until August 1st: 0800 0222 320, After August 1st: +3215280570',
    CA: '1-888-335-6644',
    CN: '4008271668',
    CZ: '(+420)228 883 543',
    DK: '(+45) 71 74 73 70',
    EG: 'Email: services@delonghi.ae',
    FI: '09 3158 2156',
    FR: 'Not available',
    DE: '06102 5790888 (After Sales), 06102 5790883 (E-commerce)',
    GR: '210 2854874',
    HK: '+852 2237 1313',
    HU: '(+36)1 8000234',
    IN: '1-800-945-0929',
    IL: '(+972) 3 6508453',
    IT: '1-800-945-0929',
    JP: '0120-835-120',
    KR: '+1-800-945-0929',
    KW: '(+965) 1802929',
    NL: 'Until August 1st: (+31) 76 7995197, After August 1st: +31 765233600',
    NO: '(+46) 8-205270',
    OM: '(+968) 24666788',
    PL: '(+48) 22 123 04 00',
    PT: '707 201 468',
    QA: '(+974) 44257844',
    RO: '0800 672 671',
    SA: 'Email: services@delonghi.ae',
    SK: '+421 233 057 876',
    ZA: '011 474 0153',
    ES: '936 065 405',
    SE: '(+46) 8-205270',
    CH: '+41(0)41 766 8727',
    TW: '0809 099905',
    TR: '0850 216 02 41',
    UA: '0800 503507',
    AE: 'Not available',
    GB: '0345 222 0458',
    US: '1-866-528-8323',
    BG: 'Not available',
}

export * from '@salesforce/retail-react-app/app/constants'
